export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  category: string;
  categoryLabel: string;
  thumbnail: string;
  excerpt: string;
  content: string;
  author: string;
  authorAvatar: string;
  createdAt: string;
  readTime: number;
  tags: string[];
  featured: boolean;
}

export const blogCategories = [
  { key: "all", label: "전체보기", icon: "ri-apps-line" },
  { key: "basics", label: "경매 기초", icon: "ri-book-open-line" },
  { key: "investment", label: "실전 투자", icon: "ri-briefcase-line" },
  { key: "rights", label: "권리분석", icon: "ri-file-search-line" },
  { key: "market", label: "부동산 시장", icon: "ri-bar-chart-box-line" },
  { key: "success", label: "성공 사례", icon: "ri-trophy-line" },
];

export const blogPosts: BlogPost[] = [
  {
    id: "blog-1",
    title: "초보자가 꼭 알아야 할 경매 투자 5가지 원칙",
    slug: "auction-basics-5-principles",
    category: "basics",
    categoryLabel: "경매 기초",
    thumbnail: "https://img.echotwin.kr/kangstudy/55eb23f367650dbb.jpg",
    excerpt: "경매 투자를 처음 시작하는 분들이 반드시 알아야 할 기본 원칙과 실수하기 쉬운 포인트를 정리했습니다. 차근차근 따라오세요.",
    content: `# 초보자가 꼭 알아야 할 경매 투자 5가지 원칙

경매 투자, 막상 시작하려면 막막하시죠? 수많은 정보 속에서 무엇부터 알아야 할지 모르겠다면, 이 글을 주목해주세요. 강스터디에서 수년간 경매 교육을 하면서 깨달은 핵심 원칙 5가지를 정리했습니다.

## 1. 권리분석은 선택이 아닌 필수

경매 물건을 볼 때 가장 먼저 확인해야 할 것은 바로 **권리분석**입니다. 매각물건명세서를 꼼꼼히 살피고, 등기부등본을 통해 선순위 권리자를 파악해야 합니다. 특히 말소기준권리보다 앞서는 권리는 인수해야 하므로 예상보다 많은 비용이 들어갈 수 있어요.

> 실제 사례: A씨는 감정가 5억 아파트가 3억에 나와 덜컥 입찰했다가, 선순위 임차보증금 8천만 원을 인수하게 되어 실질 낙찰가는 3.8억이 되어버린 경우가 있습니다.

## 2. 현장 방문을 게을리하지 마세요

사진만 보고 판단하면 큰코다칩니다. 반드시 현장에 가서 주변 환경, 소음, 일조량, 실제 건물 상태를 확인해야 합니다. 권리분석이 완벽해도 현장에서 예상치 못한 하자가 발견되면 수익률이 급감할 수 있어요.

## 3. 입찰가 산정은 보수적으로

감정가의 80% 수준에서 최저입찰가가 결정되더라도, 낙찰 후 추가 비용을 고려해 보수적으로 입찰가를 설정하세요. 취득세, 명도비용, 수리비, 공실 기간을 모두 계산에 넣어야 실제 수익을 정확히 파악할 수 있습니다.

## 4. 명도 전략을 미리 세우세요

낙찰 후 가장 큰 변수가 바로 명도입니다. 점유자가 자진 퇴거할지, 강제집행이 필요할지에 따라 비용과 시간이 크게 달라집니다. 점유자 유형(임차인/소유자/무단점유)에 따른 명도 전략을 미리 세워두는 것이 중요합니다.

## 5. 한 번에 큰 수익을 바라지 마세요

경매는 '한 방'이 아닌 꾸준한 투자입니다. 첫 투자에서 큰 수익을 기대하기보다, 소액으로 시작해 경험을 쌓고 점차 규모를 키워가는 전략이 훨씬 안전합니다. 경매 고수들도 평균 수익률 10~15%를 목표로 합니다.

---

경매 공부가 막막하게 느껴지신다면, 강스터디의 무료 입문 강의부터 시작해보세요. 차근차근 기초를 다지다 보면 어느새 경매 물건이 보이기 시작할 거예요.

다음 글에서는 '권리분석 완벽 가이드'로 찾아뵙겠습니다.`,
    author: "강현우",
    authorAvatar: "https://img.echotwin.kr/kangstudy/0187aca3fddc8650.jpg",
    createdAt: "2026.07.25",
    readTime: 5,
    tags: ["경매입문", "초보자", "투자원칙"],
    featured: true,
  },
  {
    id: "blog-2",
    title: "권리분석 완벽 가이드: 등기부등본 읽는 법",
    slug: "rights-analysis-complete-guide",
    category: "rights",
    categoryLabel: "권리분석",
    thumbnail: "https://img.echotwin.kr/kangstudy/98cc4accfa7e09bc.jpg",
    excerpt: "등기부등본, 어렵게만 느껴지시나요? 이 글 하나로 갑구·을구·병구 완전 정복! 실제 등기부등본 예시와 함께 설명합니다.",
    content: `# 권리분석 완벽 가이드: 등기부등본 읽는 법

등기부등본만 제대로 읽을 수 있어도 경매 투자의 절반은 성공입니다. 오늘은 갑구, 을구, 병구로 나뉘는 등기부등본의 구조와 실제 분석 방법을 알려드립니다.

## 등기부등본의 구조

### 갑구 (소유권에 관한 사항)
소유자의 인적사항과 소유권 변동 이력을 확인할 수 있습니다. 소유권 보존, 소유권 이전, 가등기, 경매개시결정 등이 여기에 기록됩니다.

### 을구 (소유권 이외의 권리)
저당권, 근저당권, 지상권, 전세권, 임차권 등이 기록됩니다. **말소기준권리**보다 뒤에 설정된 권리는 낙찰 시 말소되지만, 앞서는 권리는 인수해야 합니다.

### 병구 (가압류/가처분)
별도 등기가 필요한 권리제한 사항을 기록합니다.

## 실전 분석 3단계

1. **말소기준권리 찾기**: 가장 먼저 실행된 강제경매/임의경매 개시결정을 찾으세요. 이것이 말소기준권리가 됩니다.

2. **인수되는 권리 체크**: 말소기준권리보다 앞선 권리는 모두 인수됩니다. 저당권이 있다면 채권최고액을 확인하세요.

3. **가압류/가처분 확인**: 병구의 가압류는 낙찰자가 인수하지 않지만, 경매 절차가 지연될 수 있습니다.

> 노하우: 등기부등본은 인터넷등기소(www.iros.go.kr)에서 누구나 발급받을 수 있어요. 수수료는 건당 700원입니다.

더 자세한 실습을 원하신다면, 강스터디 '권리분석 마스터 과정'을 수강해보세요. 실제 등기부등본 50건을 함께 분석하는 강의입니다.`,
    author: "강현우",
    authorAvatar: "https://img.echotwin.kr/kangstudy/0187aca3fddc8650.jpg",
    createdAt: "2026.07.22",
    readTime: 7,
    tags: ["권리분석", "등기부등본", "실전노하우"],
    featured: true,
  },
  {
    id: "blog-3",
    title: "2026년 하반기 부동산 경매 시장 전망",
    slug: "2026-half-year-market-outlook",
    category: "market",
    categoryLabel: "부동산 시장",
    thumbnail: "https://img.echotwin.kr/kangstudy/b38631dc7f609211.jpg",
    excerpt: "금리 인하 기대감과 수도권 공급 부족이 맞물린 2026년 하반기. 경매 시장은 어떤 흐름을 보일지 데이터로 분석했습니다.",
    content: `# 2026년 하반기 부동산 경매 시장 전망

2026년 상반기가 마무리되고 하반기를 맞이하는 지금, 부동산 경매 시장의 주요 지표들을 살펴보며 하반기 전망을 공유합니다.

## 주요 지표 분석

### 경매 진행 건수
2026년 상반기 전국 법원 경매 진행 건수는 약 8만 2천 건으로, 전년 동기 대비 12% 증가했습니다. 고금리 장기화로 인한 채무 불이행 증가가 주요 원인으로 분석됩니다.

### 평균 낙찰가율
수도권 아파트 평균 낙찰가율은 84.3%로, 2025년 82.1% 대비 소폭 상승했습니다. 서울 강남권은 89.7%로 여전히 높은 경쟁률을 보이고 있습니다.

### 평균 응찰자 수
전국 평균 3.2명으로 전년 대비 0.5명 증가. 수도권 인기 물건은 10명 이상이 몰리는 경우도 많습니다.

## 하반기 전망 포인트

1. **금리 인하 가능성**: 한국은행의 기준금리 인하 시그널이 감지되면 경매 낙찰가율은 더욱 상승할 전망입니다.

2. **수도권 공급 부족**: 신축 아파트 입주 물량 감소가 예정되어 있어, 기존 주택 경매 물건에 대한 수요가 증가할 것으로 보입니다.

3. **상가·오피스텔 주의**: 경기 불확실성으로 상업용 부동산 경매는 여전히 리스크가 큽니다. 수익률 계산을 더욱 보수적으로 하세요.

종합적으로 볼 때, **수도권 중소형 아파트**가 가장 안정적인 투자처가 될 것으로 예상됩니다. 하지만 어떤 물건이든 권리분석과 현장 실사는 필수입니다.`,
    author: "박지원",
    authorAvatar: "https://img.echotwin.kr/kangstudy/1da610ed81a89463.jpg",
    createdAt: "2026.07.18",
    readTime: 4,
    tags: ["시장분석", "2026전망", "데이터"],
    featured: false,
  },
  {
    id: "blog-4",
    title: "실전 투자자의 경매 성공기: 월세 수익 月 200만원 만들기",
    slug: "success-story-monthly-rent-2m",
    category: "success",
    categoryLabel: "성공 사례",
    thumbnail: "https://img.echotwin.kr/kangstudy/1ba30d0b527c9416.jpg",
    excerpt: "경매로 낙찰받은 아파트를 리모델링해 월세 200만원을 만들기까지의 과정을 상세히 공개합니다. 초보 투자자도 따라 할 수 있어요!",
    content: `# 실전 투자자의 경매 성공기: 월세 수익 月 200만원 만들기

강스터디 수강생 김모 씨(38세, 직장인)의 실제 경매 투자 사례를 소개합니다. 경매 초보였던 그가 어떻게 안정적인 월세 수익을 만들었는지, 단계별로 공개합니다.

## 물건 발견

- **소재지**: 경기도 화성시 동탄신도시
- **물건 유형**: 아파트 (전용 84㎡)
- **감정가**: 6.2억
- **최저입찰가**: 4.34억 (70% — 3회 유찰)

## 권리분석 결과

| 항목 | 내용 |
|------|------|
| 근저당권 | 있음 (말소기준권리 이후 — 낙찰 시 말소) |
| 임차인 | 세입자 1명 (보증금 2억) |
| 점유 상태 | 임차인 거주 중 |

임차보증금 2억원은 낙찰자가 인수해야 하는 조건이었습니다. 하지만 이 부분을 투자 금액에 반영해 계산한 결과, 여전히 매력적인 수익률이 나왔습니다.

## 입찰 전략

- 입찰가: 4.5억 (감정가 대비 72.6%)
- 임차보증금 인수: 2억
- 실질 투자금: 6.5억
- 예상 시세: 7.5~8억

## 명도 및 리모델링

임차인은 계약 기간이 6개월 남아 있어 자연스럽게 퇴거했습니다. 이후 1,500만원을 들여 도배, 장판, 조명 교체 등 가벼운 리모델링을 진행했습니다.

## 수익 현황

현재 보증금 2억 / 월세 200만원에 임대 중입니다. 연간 수익률은 실질 투자금 6.65억(매입 6.5억 + 리모델링 0.15억) 기준 약 3.6%로, 은행 금리 대비 우수한 성과입니다. 게다가 시세 차익을 고려하면 자본 수익률은 더욱 높아집니다.

> 핵심 포인트: 남들이 꺼리는 유찰 3회 이상 물건에서 기회를 찾으세요. 권리분석만 철저히 하면 리스크 대비 높은 수익을 기대할 수 있습니다.

김모 씨는 현재 두 번째 경매 물건을 준비 중입니다. 여러분도 할 수 있습니다!`,
    author: "강현우",
    authorAvatar: "https://img.echotwin.kr/kangstudy/0187aca3fddc8650.jpg",
    createdAt: "2026.07.15",
    readTime: 6,
    tags: ["성공사례", "월세수익", "실전투자"],
    featured: true,
  },
  {
    id: "blog-5",
    title: "경매 명도 전략 A to Z: 점유자 유형별 대응법",
    slug: "auction-eviction-strategy-guide",
    category: "investment",
    categoryLabel: "실전 투자",
    thumbnail: "https://img.echotwin.kr/kangstudy/398e0984770fba3a.jpg",
    excerpt: "낙찰은 끝이 아닌 시작! 점유자 유형(임차인, 소유자, 무단점유)별 명도 전략과 예상 비용을 상세히 정리했습니다.",
    content: `# 경매 명도 전략 A to Z: 점유자 유형별 대응법

"낙찰받았는데, 세입자가 안 나가면 어떡하죠?" 가장 많이 받는 질문입니다. 오늘은 점유자 유형별 명도 전략을 완벽하게 정리해드립니다.

## 점유자 유형 1: 임차인

가장 흔한 유형입니다. 낙찰자가 임대인의 지위를 승계하므로 아래 사항을 확인하세요:

- **대항력 있는 임차인**: 주택임대차보호법에 따라 보증금을 인수해야 합니다. 보증금 반환 후 퇴거를 요청할 수 있습니다.
- **대항력 없는 임차인**: 법적 보호를 받지 못하므로 퇴거 협의가 수월합니다.
- **예상 비용**: 보증금 인수 범위 내. 보통 2,000만~2억 원

## 점유자 유형 2: 소유자(채무자)

낙찰 후에도 계속 거주하는 소유자입니다.

- **자진 퇴거 협의**: 이사비용 지원(200~500만원) 조건으로 자진 퇴거를 유도하는 것이 가장 경제적입니다.
- **명도소송**: 불응 시 인도명령 소송 제기. 통상 3~6개월 소요됩니다.
- **강제집행**: 법원 집행관을 통한 강제 퇴거. 추가 비용 100~300만원
- **예상 총비용**: 300~1,000만원

## 점유자 유형 3: 무단점유자

권한 없이 점유 중인 경우입니다.

- **형사고소 가능**: 주거침입죄, 퇴거불응죄로 고소할 수 있습니다.
- **명도소송**: 민사상 인도청구 소송
- **예상 비용**: 변호사 비용 300~500만원, 소송 기간 3~6개월

> 꿀팁: 낙찰 전에 반드시 현장 방문해 점유자와 대화를 시도하세요. 무리한 요구가 있는 물건은 입찰을 포기하는 것도 전략입니다.

명도에 자신 없다면, 강스터디의 '명도 실전 노하우' 강의를 통해 사례별 대처법을 배워보세요.`,
    author: "김태호",
    authorAvatar: "https://img.echotwin.kr/kangstudy/e25e57e435685591.jpg",
    createdAt: "2026.07.10",
    readTime: 8,
    tags: ["명도전략", "실전노하우", "점유자대응"],
    featured: false,
  },
  {
    id: "blog-6",
    title: "입찰가 산정 비법: 일반방식 vs 시세역산방식 비교 분석",
    slug: "bid-calculation-normal-vs-reverse",
    category: "investment",
    categoryLabel: "실전 투자",
    thumbnail: "https://img.echotwin.kr/kangstudy/9532120ff35e4333.jpg",
    excerpt: "감정가 기반의 일반방식과 급매시세 기반의 시세역산방식, 어떤 상황에서 어떤 계산법을 선택해야 할지 실제 데이터로 비교했습니다.",
    content: `# 입찰가 산정 비법: 일반방식 vs 시세역산방식 비교 분석

입찰가 계산, 어떤 방식으로 하시나요? 오늘은 강스터디 입찰계산기의 두 가지 핵심 방식을 실제 사례로 비교 분석합니다.

## 일반방식 (비용 공제형)

**공식**: 추천 입찰가 = 예상 매도가 - (제반비용 + 목표 수익)

감정가와 최저입찰가를 기준으로 아래에서 위로 쌓아 올리는 방식입니다. 경매 초보자가 이해하기 쉽고, 보수적인 접근이 가능합니다.

### 장점
- 기준점(감정가)이 명확해 계산이 직관적
- 모든 부대비용을 빠짐없이 반영 가능
- 안전마진 확보가 용이

### 단점
- 감정가가 시세를 반영하지 못하는 경우 오차 발생
- 급매 물건의 기회를 놓칠 수 있음

## 시세역산방식

**공식**: P = V(급매시세) - M(목표마진) - C(제반비용) - R(인수권리금)

급매 시세에서 원하는 마진을 빼서 최대 투찰 가능 금액을 역산합니다. Top-Down 방식으로, 경매 고수들이 선호합니다.

### 장점
- 시장 상황을 즉시 반영
- 실제 수익 가능성을 직관적으로 파악
- 마지노선(투찰 상한가)이 명확

### 단점
- 정확한 급매 시세 파악이 어려움
- 과도한 낙관론에 빠질 위험

## 실제 사례로 비교

| 항목 | 일반방식 | 시세역산방식 |
|------|----------|--------------|
| 감정가 6억, 최저 4.2억, 급매시세 7.5억 | 권장 투찰: 5.2억 | 투찰 상한: 5.8억 |
| 예상 ROI | +15.4% | +12.1% |
| 낙찰 가능성 | 중간 | 높음 |

## 어떤 방식을 선택해야 할까?

- **초보자라면**: 일반방식으로 시작해 감을 잡으세요.
- **급매 물건이라면**: 시세역산으로 실제 가치를 평가하세요.
- **두 방식 모두 사용하세요!** 두 계산 결과를 비교하면 더 균형 잡힌 판단이 가능합니다.

지금 바로 강스터디 [입찰계산기](/bid-calculator)에서 두 방식을 모두 체험해보세요!`,
    author: "박지원",
    authorAvatar: "https://img.echotwin.kr/kangstudy/1da610ed81a89463.jpg",
    createdAt: "2026.07.05",
    readTime: 5,
    tags: ["입찰가산정", "계산기", "비교분석"],
    featured: false,
  },
  {
    id: "blog-7",
    title: "경매로 내 집 마련하기: 30대 직장인의 첫 경매 도전기",
    slug: "first-auction-home-purchase-30s",
    category: "success",
    categoryLabel: "성공 사례",
    thumbnail: "https://img.echotwin.kr/kangstudy/c0ec3627db450acc.jpg",
    excerpt: "전세 대신 경매로 내 집을 마련한 30대 직장인의 실제 이야기. 투자 목적이 아닌 실거주 목적의 경매 접근법을 소개합니다.",
    content: `# 경매로 내 집 마련하기: 30대 직장인의 첫 경매 도전기

"경매는 전문 투자자만 하는 거 아닌가요?" 많은 분들이 이렇게 생각하시죠. 하지만 오늘 소개할 이모 씨(34세, 회사원)는 실거주 목적으로 경매에 도전해 내 집 마련에 성공했습니다.

## 시작 계기

전세 대출 이자가 계속 오르면서 '차라리 내 집을 사자'는 결심을 했습니다. 하지만 일반 매매로는 서울에서 30평대 아파트가 엄두가 안 났죠. 그러던 중 유튜브에서 경매 관련 영상을 보고 가능성을 발견했습니다.

## 준비 과정

1. **3개월간 강스터디에서 공부**: 무료 입문 강의 → 권리분석 마스터 과정 → 실전반 수강
2. **경매 법원 5회 이상 방문**: 실제 경매 현장 분위기 체험
3. **관심 물건 10개 리스트업**: 매주 법원 경매 사이트에서 관심 지역 물건 모니터링

## 낙찰 물건 정보

- **소재지**: 서울 관악구 봉천동
- **물건 유형**: 아파트 (전용 59㎡)
- **감정가**: 5.8억
- **최저입찰가**: 4.06억 (70%)
- **실제 낙찰가**: 4.55억 (감정가 대비 78.4%)

## 권리분석

| 항목 | 결과 |
|------|------|
| 근저당권 | 말소 가능 |
| 임차인 | 소액 임차인 없음 |
| 명도 | 소유자(채무자) 거주 → 이사비 지원 조건 협의 성공 |

## 수익(절감) 분석

- **일반 매매 시세**: 6.5억
- **낙찰가 + 비용**: 4.55억(낙찰) + 0.12억(취득세 등) + 0.05억(명도·수리) = 4.72억
- **절감 금액**: 약 1.78억 (시세 대비 27% 절감)

> "처음엔 무서웠지만, 강스터디에서 차근차근 배우고 나니 할 만하다는 자신감이 생겼어요. 덕분에 예산보다 훨씬 좋은 집을 구했어요." — 이모 씨

이모 씨처럼 당신도 할 수 있습니다. 강스터디 무료 입문 강의로 첫걸음을 시작해보세요!`,
    author: "김태호",
    authorAvatar: "https://img.echotwin.kr/kangstudy/e25e57e435685591.jpg",
    createdAt: "2026.06.28",
    readTime: 5,
    tags: ["성공사례", "내집마련", "초보자"],
    featured: false,
  },
  {
    id: "blog-8",
    title: "경매 투자자가 알아야 할 세금 완벽 정리",
    slug: "auction-tax-complete-guide",
    category: "basics",
    categoryLabel: "경매 기초",
    thumbnail: "https://img.echotwin.kr/kangstudy/5523099709a06781.jpg",
    excerpt: "취득세, 재산세, 양도소득세까지! 경매로 부동산을 취득할 때 발생하는 모든 세금을 한눈에 정리했습니다.",
    content: `# 경매 투자자가 알아야 할 세금 완벽 정리

경매로 물건을 낙찰받은 후 예상보다 세금이 많이 나와 당황하신 적 있으신가요? 오늘은 경매 투자자가 반드시 알아야 할 세금을 완벽 정리합니다.

## 1. 취득세

낙찰 즉시 발생하는 가장 큰 세금입니다.

- **주택(6억 이하)**: 취득가액의 1.1%
- **주택(6~9억)**: 취득가액의 1.1~2.0%
- **주택(9억 초과)**: 취득가액의 3.0%
- **상가/토지 등**: 취득가액의 4.6%

취득가액 = 낙찰가격 + 인수한 보증금 등

> 실제 예시: 4.5억 아파트 낙찰 시, 취득세 = 4.5억 × 1.1% = 495만원 (임차보증금 인수 시 추가)

## 2. 재산세

매년 7월과 9월에 부과됩니다.

- **주택**: 공시가격 기준 과세표준에 따라 세율 적용 (0.1~0.4%)
- **토지**: 공시지가 기준 0.2~0.5%

## 3. 양도소득세

매도 시 발생하는 차익에 대한 세금입니다.

- **단기 보유(1년 미만)**: 40%
- **단기 보유(1~2년)**: 기본 세율
- **장기 보유(2년 이상)**: 기본 세율 + 장기보유특별공제

## 입찰 시 반드시 반영하세요!

강스터디 [입찰계산기](/bid-calculator)의 '제반비용' 항목에는 취득세가 자동 반영됩니다. 계산기와 함께라면 세금 때문에 당황할 일이 없습니다.`,
    author: "박지원",
    authorAvatar: "https://img.echotwin.kr/kangstudy/1da610ed81a89463.jpg",
    createdAt: "2026.06.20",
    readTime: 5,
    tags: ["세금", "취득세", "기초지식"],
    featured: false,
  },
  {
    id: "blog-9",
    title: "경매 투자, 성공하는 사람들의 7가지 습관",
    slug: "successful-auction-investor-7-habits",
    category: "investment",
    categoryLabel: "실전 투자",
    thumbnail: "https://img.echotwin.kr/kangstudy/1386eac0e5823518.jpg",
    excerpt: "경매로 꾸준히 수익을 내는 사람들의 공통된 습관을 분석했습니다. 단순한 기술이 아닌 마인드셋과 루틴의 차이입니다.",
    content: `# 경매 투자, 성공하는 사람들의 7가지 습관

강스터디에서 수년간 수많은 수강생들을 만나며 발견한 공통점이 있습니다. 성공하는 경매 투자자들은 단순히 '잘 아는 것'을 넘어 특별한 습관을 가지고 있다는 것입니다.

## 1. 매일 법원 경매 사이트 확인

아침 루틴으로 30분간 새로 올라온 물건을 스캔합니다. 좋은 물건은 빨리 사라집니다.

## 2. 엑셀로 투자 일지 작성

물건 분석 내용, 낙찰 결과, 수익률을 기록합니다. 데이터가 쌓이면 패턴이 보입니다.

## 3. 현장 방문을 루틴화

관심 물건은 무조건 주말에 현장 답사합니다. 사진으로는 모르는 '냄새'와 '분위기'를 직접 느끼는 것이 중요합니다.

## 4. 커뮤니티 활용

혼자 공부하지 않습니다. 강스터디 커뮤니티나 오프라인 스터디에서 정보를 공유하며 시야를 넓힙니다.

## 5. 입찰가에 감정 배제

"꼭 이 물건이어야 해"라는 집착을 버립니다. 계산된 가격 이상은 과감히 포기하는 냉정함이 필요합니다.

## 6. 매월 부동산 시장 리포트 읽기

한국부동산원, KB부동산 등 신뢰할 수 있는 기관의 리포트를 정기적으로 읽습니다.

## 7. 실패에서 배우기

낙찰 실패도 소중한 데이터입니다. 왜 실패했는지 분석하고 다음 입찰에 반영합니다.

이 7가지 습관, 오늘부터 하나씩 실천해보세요. 꾸준함이 결국 수익을 만듭니다.`,
    author: "강현우",
    authorAvatar: "https://img.echotwin.kr/kangstudy/0187aca3fddc8650.jpg",
    createdAt: "2026.06.15",
    readTime: 4,
    tags: ["성공습관", "마인드셋", "노하우"],
    featured: false,
  },
];

export const getFeaturedPosts = () => blogPosts.filter((p) => p.featured);
export const getRecentPosts = (excludeId?: string) => 
  blogPosts
    .filter((p) => p.id !== excludeId)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());